import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Save, Trash2, Upload, ChevronDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CATEGORIES = ["Sales", "Business Mastery", "Ecommerce", "Offers & Pricing"];
const TYPES = ["Book", "Mini-Course", "Guide", "Checklist", "Listicle", "Prompt Pack", "Audio"];

function ProgramCard({ p, invalidate }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: p.name, category: p.category, description: p.description || "", cover_image: p.cover_image || "", order: p.order ?? 99 });
  const [newAsset, setNewAsset] = useState({ title: "", type: "Book" });
  const fileRefs = useRef({});

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd);
    return data.url;
  };

  const saveProgram = async () => {
    try { await api.put(`/library/admin/programs/${p.id}`, { ...form, order: parseInt(form.order) || 99 }); toast.success("Saved"); invalidate(); }
    catch { toast.error("Save failed"); }
  };

  const saveAsset = async (a, updates) => {
    try { await api.put(`/library/admin/assets/${a.id}`, { ...a, ...updates }); toast.success(`Saved ${a.title}`); invalidate(); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`lib-admin-program-${p.id}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-5 py-4 text-left" data-testid={`lib-admin-toggle-${p.id}`}>
        <div>
          <p className="text-sm font-bold text-navy">{p.name}</p>
          <p className="text-xs text-slate-500">{p.category} · {p.assets?.length || 0} assets · {p.assets?.filter((a) => a.file_url).length || 0} with files</p>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="space-y-4 border-t border-slate-100 px-5 py-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="lg:col-span-2"><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label className="text-xs">Category</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select></div>
            <div><Label className="text-xs">Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="flex items-center gap-2">
            <Button onClick={saveProgram} size="sm" className="rounded-md bg-navy text-white" data-testid={`lib-admin-save-${p.id}`}><Save className="mr-1.5 h-3.5 w-3.5" /> Save program</Button>
            <Button onClick={async () => { await api.delete(`/library/admin/programs/${p.id}`); toast.success("Deleted"); invalidate(); }} size="sm" variant="ghost" className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Assets</p>
            {(p.assets || []).map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2" data-testid={`lib-admin-asset-${a.id}`}>
                <select value={a.type} onChange={(e) => saveAsset(a, { type: e.target.value })} className="h-8 rounded-md border border-input bg-background px-2 text-xs">
                  {TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                <Input defaultValue={a.title} onBlur={(e) => e.target.value !== a.title && saveAsset(a, { title: e.target.value })} className="h-8 min-w-[200px] flex-1 text-xs" />
                <input ref={(el) => (fileRefs.current[a.id] = el)} type="file" className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    try { const url = await upload(f); await saveAsset(a, { file_url: url, file_name: f.name }); toast.success("File uploaded"); }
                    catch { toast.error("Upload failed"); }
                    e.target.value = "";
                  }} />
                <Button size="sm" variant="outline" className="h-8 rounded-md text-xs" onClick={() => fileRefs.current[a.id]?.click()} data-testid={`lib-admin-upload-${a.id}`}>
                  <Upload className="mr-1 h-3 w-3" /> {a.file_url ? "Replace file" : "Upload file"}
                </Button>
                {a.file_url && <span className="text-[11px] text-emerald">✓ {a.file_name || "uploaded"}</span>}
                <Button size="sm" variant="ghost" className="h-8 text-red-500" onClick={async () => { await api.delete(`/library/admin/assets/${a.id}`); invalidate(); }}><Trash2 className="h-3 w-3" /></Button>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <select value={newAsset.type} onChange={(e) => setNewAsset({ ...newAsset, type: e.target.value })} className="h-8 rounded-md border border-input bg-background px-2 text-xs">
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <Input placeholder="New asset title" value={newAsset.title} onChange={(e) => setNewAsset({ ...newAsset, title: e.target.value })} className="h-8 min-w-[200px] flex-1 text-xs" data-testid={`lib-admin-newasset-${p.id}`} />
              <Button size="sm" variant="outline" className="h-8 rounded-md text-xs" disabled={!newAsset.title}
                onClick={async () => { await api.post(`/library/admin/programs/${p.id}/assets`, newAsset); setNewAsset({ title: "", type: "Book" }); toast.success("Asset added"); invalidate(); }}>
                <Plus className="mr-1 h-3 w-3" /> Add asset
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function LibraryAdmin() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["lib-admin"] });
  const { data } = useQuery({ queryKey: ["lib-admin"], queryFn: async () => (await api.get("/library/admin/programs")).data });
  const [np, setNp] = useState({ name: "", category: "Sales" });

  return (
    <div className="space-y-4" data-testid="library-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-xl font-bold text-navy">The Library</h3>
          <p className="text-sm text-slate-500">{data?.length || 0} programs · ₹499 tripwire · upload files per asset, add programs without a redeploy.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="New program name" value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} className="w-56" data-testid="lib-admin-new-program" />
          <select value={np.category} onChange={(e) => setNp({ ...np, category: e.target.value })} className="h-9 rounded-md border border-input bg-background px-3 text-sm">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <Button size="sm" className="rounded-md bg-emerald text-white" disabled={!np.name}
            onClick={async () => { await api.post("/library/admin/programs", np); setNp({ name: "", category: "Sales" }); toast.success("Program added"); invalidate(); }} data-testid="lib-admin-add-program">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add program
          </Button>
        </div>
      </div>
      <div className="space-y-3">
        {(data || []).map((p) => <ProgramCard key={p.id} p={p} invalidate={invalidate} />)}
      </div>
    </div>
  );
}
